import { useState, useEffect, useMemo } from "react";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { format, startOfDay } from "date-fns";
import { CalendarIcon, Calculator, Target, Zap, Building2, Users, Eye, Wallet, AlertCircle, Loader2, Split } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import PVCalculationModal from "./PVCalculationModal";
import { parseLocalDateInput } from "../../lib/dateFormat";
import { cn } from "../../lib/utils";
import { FundingType, NoticeOfAssignment, ScheduleLine } from "../../types/schema";
import { calculateFundingCap, selectPeriodsToFitFunding, calculatePresentValueGoalSeek } from "../../lib/noa-calculations";
import { Schedule } from "../../store/appStore";
import { toast } from "../../hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
// import CustomDatePicker from "../SimpleDatePicker";
import CustomDatePicker from "../ui/calendar1";
import apiService from "../../services/apiService";

interface Props {
  children: React.ReactNode;
  approvedSchedules?: Schedule[];
  // Backwards compatible: previously passed customer-wise lenders
  addNoA: (noa: NoticeOfAssignment) => void;
}

/** Regular rental periods only — interim (`periodNo` / `i`) excluded from NoA assignment. */
const isAssignableRentalLine = (line: { periodNo?: string | number; isInterim?: boolean }) =>
  !line.isInterim && String(line.periodNo).toLowerCase() !== "i";

/** Only unset or Pending lines can be assigned on a new NoA. */
const isSelectableRentalLine = (line: { periodNo?: string | number; isInterim?: boolean; status?: string | null }) =>
  isAssignableRentalLine(line) &&
  (line.status == null || line.status === "" || String(line.status).toLowerCase() === "pending");

export default function NoAGeneratorDrawer({ children, approvedSchedules: propApprovedSchedules, addNoA }: Props) {
  // const { addNoA } = useAppStore();
  const [open, setOpen] = useState(false);

  // Dynamic data - use props if provided, otherwise fallback to store
  const approvedSchedules = propApprovedSchedules;
  const [availableLenders, setAvailableLenders] = useState<any[]>([]);
  const [isLendersLoading, setIsLendersLoading] = useState(false);

  // Form state
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [selectedLenderId, setSelectedLenderId] = useState("");
  const [selectedLender, setSelectedLender] = useState<any>(null);
  const [fundingLimitPercent, setFundingLimitPercent] = useState(80);
  const [fundingTypeOverride, setFundingTypeOverride] = useState<FundingType | "">("");
  const [discountRate, setDiscountRate] = useState(12);
  const [assignmentDate, setAssignmentDate] = useState<string>("");
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Partial Rental Assignment — available for Pre GST, Post GST & Post TDS.
  // Cap per period depends on funding type:
  //   Pre GST   → Base
  //   Post GST  → Base + GST
  //   Post TDS  → Base + GST − TDS
  const [isPartialAssignment, setIsPartialAssignment] = useState(false);
  const [lenderShares, setLenderShares] = useState<Record<string, number>>({});
  const [bulkAmount, setBulkAmount] = useState<string>("");
  const [bulkFrom, setBulkFrom] = useState<string>("");
  const [bulkTo, setBulkTo] = useState<string>("");

  // Lender Funding Amount - can be entered as amount or percentage
  const [lenderFeeType, setLenderFeeType] = useState<"amount" | "percentage">("amount");
  const [lenderFeeAmount, setLenderFeeAmount] = useState<number>(0);
  const [lenderFeePercent, setLenderFeePercent] = useState<number>(0);

  const assignableSchedules = useMemo(
    () => approvedSchedules?.filter((s) => s.lines?.some(isAssignableRentalLine)) ?? [],
    [approvedSchedules]
  );

  const selectedSchedule = assignableSchedules.find((s) => s.id === selectedScheduleId);

  const assignableLines = useMemo(
    () => selectedSchedule?.lines.filter(isAssignableRentalLine) ?? [],
    [selectedSchedule]
  );

  const selectableLines = useMemo(
    () => assignableLines.filter(isSelectableRentalLine),
    [assignableLines]
  );

  // Drop interim / non-pending selections when schedule changes
  useEffect(() => {
    if (!selectedSchedule) {
      setSelectedPeriods([]);
      return;
    }
    const validIds = new Set(selectableLines.map((l) => l.scheduleLineId));
    setSelectedPeriods((prev) => prev.filter((id) => validIds.has(id)));
  }, [selectedScheduleId, selectedSchedule, selectableLines]);

  // Fetch lenders by schedule id whenever schedule changes
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!selectedScheduleId) {
        setAvailableLenders([]);
        setSelectedLenderId("");
        setSelectedLender(null);
        return;
      }
      setIsLendersLoading(true);
      try {
        // Expected API: returns lenders for the selected schedule
        // If your backend uses a different path, adjust it here.
        const res: any = await apiService.get<any>(`/lender/get-tagged-lenders-by-schedule/${selectedScheduleId}`);
        const data = res?.data ?? res?.result ?? res;
        if (!cancelled) {
          setAvailableLenders(Array.isArray(data) ? data : []);
          // reset lender selection when schedule changes
          setSelectedLenderId("");
          setSelectedLender(null);
        }
      } catch (e) {
        console.error("Failed to fetch lenders by schedule:", e);
        if (!cancelled) {
          setAvailableLenders([]);
          setSelectedLenderId("");
          setSelectedLender(null);
        }
      } finally {
        if (!cancelled) setIsLendersLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedScheduleId]);

  // Effective funding type — user override wins over schedule default
  const effectiveFundingType: FundingType =
    (fundingTypeOverride || selectedSchedule?.config.fundingType || "postGstDiscounting") as FundingType;
  const isOverridden =
    !!selectedSchedule &&
    !!fundingTypeOverride &&
    fundingTypeOverride !== selectedSchedule.config.fundingType;

  const lenderFundsGST =
    effectiveFundingType === "postGstDiscounting" || effectiveFundingType === "postTdsDiscounting";
  const lenderFundsTDS = effectiveFundingType === "postTdsDiscounting";

  // Set assignment date + init funding type override when schedule is selected
  useEffect(() => {
    if (selectedSchedule?.config.commencementDate) {
      const d = parseLocalDateInput(String(selectedSchedule.config.commencementDate).trim());
      if (!Number.isNaN(d.getTime())) setAssignmentDate(format(d, "yyyy-MM-dd"));
    }
    if (selectedSchedule?.config.interestRatePercent !== undefined) {
      setDiscountRate(selectedSchedule.config.interestRatePercent * 100);
    }
    if (selectedSchedule?.config.fundingType) {
      setFundingTypeOverride(selectedSchedule.config.fundingType);
    }
  }, [selectedSchedule]);

  // Live calculations with updated GST funding logic
  const baseAssetValue = selectedSchedule?.assets.reduce((sum, asset) => sum + (asset.basePrice * (asset.quantity ?? 1)), 0) || 0;
  // const totalAssetValue = baseAssetValue * 1.18;
  // Total asset value = sum(base + GST) per asset (assets can have different GST rates)
  const totalAssetValue =
    selectedSchedule?.assets.reduce((sum, asset) => {
      const qty = asset.quantity ?? 1;
      const base = (asset.basePrice ?? 0) * qty;
      const gstRate = asset.gstRate ?? 0;
      const gst = base * (gstRate / 100);
      return sum + base + gst;
    }, 0) || 0;

  const fundingCap = calculateFundingCap(totalAssetValue, fundingLimitPercent);
  const selectedLines = selectedSchedule?.lines.filter(l => selectedPeriods.includes(l.scheduleLineId)) || [];

  const isPreGST = effectiveFundingType === "preGstDiscounting";
  const isPostGST = effectiveFundingType === "postGstDiscounting";
  const isPostTDS = effectiveFundingType === "postTdsDiscounting";

  // Per-line assignable cap based on funding type
  const capForLine = (l: ScheduleLine) => {
    const gst = l.gstRaw ?? 0;
    if (isPostGST) return l.baseRentRounded + gst;
    if (isPostTDS) return l.baseRentRounded + gst - (l.tdsRounded ?? 0);
    return l.baseRentRounded; // Pre GST
  };

  const partialActive = isPartialAssignment;

  /** Per-line assignment status: A = fully assigned, P = partial, N = not assigned */
  const getAssignmentStatus = (
    lineId: string,
    share: number,
    cap: number,
    isSelected: boolean
  ): "A" | "P" | "N" => {
    if (!isSelected) return "N";
    if (partialActive && share < cap - 0.01) return "P";
    return "A";
  };

  /** Set row to A / P / N and reset amounts for that row. */
  const handleAssignedStatusChange = (
    lineId: string,
    status: "A" | "P" | "N",
    cap: number
  ) => {
    if (status === "N") {
      setSelectedPeriods((prev) => prev.filter((id) => id !== lineId));
      setLenderShares((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      return;
    }

    setSelectedPeriods((prev) => (prev.includes(lineId) ? prev : [...prev, lineId]));

    if (status === "A" || !partialActive) {
      // Fully assigned — lender share = full cap
      setLenderShares((prev) => ({ ...prev, [lineId]: cap }));
      return;
    }

    // P — reset amount so user can re-enter the partial share
    setLenderShares((prev) => ({ ...prev, [lineId]: 0 }));
  };

  const buildLineAssignment = (l: ScheduleLine, isSelected: boolean) => {
    const cap = capForLine(l);
    if (!isSelected) {
      return { lenderShare: 0, astrafinRetained: 0, assignedStatus: "N" as const };
    }
    const rawShare = partialActive
      ? (lenderShares[l.scheduleLineId] ?? cap)
      : cap;
    const share = Math.max(0, Math.min(rawShare, cap));
    const retained = Math.max(0, cap - share);
    const assignedStatus = getAssignmentStatus(l.scheduleLineId, share, cap, true);
    return { lenderShare: share, astrafinRetained: retained, assignedStatus };
  };

  // When partial-assignment is active, clone lines with baseRentRounded
  // replaced by the per-period lender share and zero out GST/TDS so PV
  // Goal Seek treats the share as the sole cashflow to lender.
  const linesForPV = partialActive
    ? selectedLines.map((l) => {
        const cap = capForLine(l);
        const share = lenderShares[l.scheduleLineId];
        const effShare = typeof share === "number" ? share : cap;
        return {
          ...l,
          baseRentRounded: Math.max(0, Math.min(effShare, cap)),
          gstRaw: 0,
          gstRounded: 0,
          gst12Rounded: 0,
          gst18Rounded: 0,
          gst28Rounded: 0,
          tdsRounded: 0,
          tdsRaw: 0,
        };
      })
    : selectedLines;

  // Use canonical Goal Seek PV calculation - operates on effective (possibly split) lines.
  const pvResult = calculatePresentValueGoalSeek(
    linesForPV,
    partialActive ? false : lenderFundsGST,
    discountRate,
    parseLocalDateInput(assignmentDate),
    partialActive ? false : lenderFundsTDS
  );
  const pvTotal = pvResult.calculatedPV;

  const variance = fundingCap - pvTotal;

  // Split summary
  const splitSummary = useMemo(() => {
    if (!partialActive) return { lender: 0, astrafin: 0, splitCount: 0, hasInvalid: false };
    let lender = 0;
    let astrafin = 0;
    let splitCount = 0;
    let hasInvalid = false;
    selectedLines.forEach((l) => {
      const cap = capForLine(l);
      const share = lenderShares[l.scheduleLineId] ?? cap;
      if (share <= 0 || share > cap) hasInvalid = true;
      if (share !== cap) splitCount++;
      lender += Math.max(0, Math.min(share, cap));
      astrafin += Math.max(0, cap - Math.min(share, cap));
    });
    return { lender, astrafin, splitCount, hasInvalid };
  }, [partialActive, selectedLines, lenderShares, effectiveFundingType]);

  // Calculate Total Funding components (PV + SD + PF)
  const securityDeposit = selectedSchedule?.upfront.securityDepositAmountRounded || 0;
  const processingFee = selectedSchedule?.upfront.processingFeeRounded || 0;
  const processingFeeGST = selectedSchedule?.upfront.feeGSTRounded || 0;
  const totalFunding = pvTotal + securityDeposit + processingFee + processingFeeGST;
  // const totalFunding = pvTotal + securityDeposit;


  // Calculate lender funding amount based on type
  const calculatedLenderFeeAmount = lenderFeeType === "percentage"
    ? (pvTotal * (lenderFeePercent / 100))
    : lenderFeeAmount;

  // Calculate shortfall/surplus
  const fundingShortfall = totalAssetValue - totalFunding;
  const shortfallPercent = totalAssetValue > 0 ? (fundingShortfall / totalAssetValue) * 100 : 0;

  // Reset form when drawer closes
  useEffect(() => {
    if (!open) {
      setSelectedScheduleId("");
      setSelectedLenderId("");
      setSelectedLender(null);
      setFundingLimitPercent(80);
      setFundingTypeOverride("");
      setDiscountRate(12);
      setAssignmentDate(format(new Date(), "yyyy-MM-dd"));
      setSelectedPeriods([]);
      setTouched({});
      setShowAllErrors(false);
      setLenderFeeType("amount");
      setLenderFeeAmount(0);
      setLenderFeePercent(0);
      setIsCreating(false);
      setIsPartialAssignment(false);
      setLenderShares({});
      setBulkAmount("");
      setBulkFrom("");
      setBulkTo("");
    }
  }, [open]);

  // Validation rules
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    if (!selectedScheduleId) errors.schedule = "Schedule is required";
    if (!selectedLenderId) errors.lender = "Lender is required";
    if (!fundingLimitPercent || fundingLimitPercent <= 0 || fundingLimitPercent > 100) errors.fundingLimit = "Funding limit must be between 1-100%";
    if (!discountRate || discountRate <= 0) errors.discountRate = "Discount rate must be greater than 0";
    if (!assignmentDate) errors.assignmentDate = "Assignment date is required";
    if (selectedScheduleId && selectedPeriods.length === 0) errors.periods = "At least one rental period must be selected";
    if (partialActive && splitSummary.hasInvalid) {
      errors.splits = "Each lender share must be greater than 0 and no more than the assignable cap for that period";
    }

    // Validate lender funding amount (optional - only validate if value is provided)
    if (lenderFeeType === "amount") {
      if (lenderFeeAmount !== undefined && lenderFeeAmount !== null && lenderFeeAmount < 0) {
        errors.lenderFeeAmount = "Lender Fee amount cannot be negative";
      }
    } else {
      if (lenderFeePercent !== undefined && lenderFeePercent !== null && (lenderFeePercent < 0 || lenderFeePercent > 100)) {
        errors.lenderFeePercent = "Lender Fee percentage must be between 0-100%";
      }
    }

    return errors;
  }, [selectedScheduleId, selectedLenderId, fundingLimitPercent, discountRate, assignmentDate, selectedPeriods.length, variance, lenderFeeType, lenderFeeAmount, lenderFeePercent, partialActive, splitSummary.hasInvalid]);

  const isFormValid = Object.keys(validationErrors).length === 0;

  const markTouched = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const shouldShowError = (field: string) => {
    return (touched[field] || showAllErrors) && validationErrors[field];
  };

  const handleFitToFunding = () => {
    if (!selectedSchedule) return;

    // Automatically fit periods to funding using Goal Seek PV calculation
    const fittedPeriods = selectPeriodsToFitFunding(
      selectableLines,
      lenderFundsGST,
      discountRate,
      parseLocalDateInput(assignmentDate),
      fundingCap,
      lenderFundsTDS
    );

    setSelectedPeriods(fittedPeriods);

    if (isPartialAssignment && selectedSchedule) {
      setLenderShares((prev) => {
        const next = { ...prev };
        fittedPeriods.forEach((id) => {
          const line = selectedSchedule.lines.find((l) => l.scheduleLineId === id);
          if (line && next[id] == null) next[id] = capForLine(line);
        });
        return next;
      });
    }

    toast({
      title: "Periods Auto-Selected",
      description: `${fittedPeriods.length} periods selected to fit within total asset value`,
    });
  };

  const handleCreateNoA = async () => {
    if (isCreating) return; // Prevent multiple calls

    setShowAllErrors(true);


    if (!isFormValid) {
      const errorMessages = Object.values(validationErrors);
      toast({
        title: "Validation Error",
        description: errorMessages[0],
        variant: "destructive",
      });
      return;
    }


    if (!selectedSchedule || !selectedLender) return;

    setIsCreating(true);

    try {
      const assignmentAsDate = parseLocalDateInput(assignmentDate);
      const validAssignmentDate = !Number.isNaN(assignmentAsDate.getTime())
        ? assignmentAsDate
        : new Date();
      const now = new Date();

      const newNoA: NoticeOfAssignment = {
        noaId: `NOA-${Date.now()}`,
        scheduleId: selectedSchedule.id,
        customer: selectedSchedule.config.customer,
        lender: selectedLender.companyName,
        lenderCompanyId: selectedLender.institution,
        fundingMode: effectiveFundingType === "preGstDiscounting" ? "A" : effectiveFundingType === "postGstDiscounting" ? "B" : "C",
        fundingLimitPercent,
        discountRate,
        assignmentDate: format(validAssignmentDate, "yyyy-MM-dd"),
        selectedRentals: selectedPeriods,
        baseAssetValue,
        totalAssetValue,
        fundingAmountCap: fundingCap,
        pvTotal,
        variance,
        upfrontRevenue: (totalFunding + (processingFee * 1.18) - calculatedLenderFeeAmount - totalAssetValue),
        status: "Draft",
        isPartialAssignment: partialActive && splitSummary.splitCount > 0 ? true : undefined,
        partialSplits: (assignableLines.length > 0 ? assignableLines : selectedLines).reduce(
          (acc, l) => {
            const isSelected = selectedPeriods.includes(l.scheduleLineId);
            acc[l.scheduleLineId] = buildLineAssignment(l, isSelected);
            return acc;
          },
          {} as Record<string, { lenderShare: number; astrafinRetained: number; assignedStatus: "A" | "P" | "N" }>
        ),
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        // Add lender funding amount (will be stored in the object, type may need update)
        lenderFeeAmount: calculatedLenderFeeAmount,
        lenderFeeType,
        lenderFeePercent: lenderFeeType === "percentage" ? lenderFeePercent : undefined,
      } as any;
      await Promise.resolve(addNoA(newNoA));

      toast({
        title: "Notice of Assignment Created",
        description: `NoA ${newNoA.noaId} created successfully`,
      });

      setOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create Notice of Assignment",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        {children}
      </DrawerTrigger>
      <DrawerContent className="h-[95vh] max-h-[95vh] ms-auto w-[95%] rounded-t-xl">
        <TooltipProvider>
          <DrawerHeader className="border-b bg-muted/30">
            <DrawerTitle>Generate Notice of Assignment</DrawerTitle>
            <DrawerDescription>
              Link rental schedule to lender with PV-based funding calculations and automated period selection
            </DrawerDescription>
          </DrawerHeader>

          <ScrollArea className="flex-1">
            <div className="space-y-6">
              {/* Top Row - Schedule & Lender Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b bg-background">
                {/* Schedule Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Select Rental Schedule <span className="text-destructive">*</span></Label>
                  <Tooltip open={!!shouldShowError('schedule')}>
                    <TooltipTrigger asChild>
                      <div>
                        <Select value={selectedScheduleId} onValueChange={(v) => { setSelectedScheduleId(v); markTouched('schedule'); }}>
                          <SelectTrigger className={cn("h-auto p-3", shouldShowError('schedule') && "border-destructive")}>
                            <SelectValue key={Math.floor(Math.random() * 10000)} placeholder="Choose an approved schedule">
                              {selectedSchedule && (
                                <div className="flex items-center justify-between w-full">
                                  <div className="text-left">
                                    <div className="font-medium">{`TS-${selectedSchedule.id}`} - Schedule #{selectedSchedule.displayId || selectedSchedule.id}</div>
                                    <div className="text-sm text-muted-foreground">{selectedSchedule.config.customer.name}</div>
                                  </div>
                                  <div className="text-right text-sm">
                                    <div className="flex items-center gap-1">
                                      <Building2 className="h-3 w-3" />
                                      ₹{totalAssetValue.toLocaleString("en-IN")}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {assignableSchedules.map((schedule) => {
                              const totalAssetValue = schedule.assets.reduce((sum, asset) => {
                                const qty = asset.quantity ?? 1;
                                const base = (asset.basePrice ?? 0) * qty;
                                const gstRate = asset.gstRate ?? 0;
                                const gst = base * (gstRate / 100);
                                return sum + base + gst;
                              }, 0) || 0;
                              return (
                                <SelectItem key={schedule.id} value={schedule.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <div>
                                      <div className="font-medium">{`TS-${schedule.id}`} - Schedule #{schedule.displayId || schedule.id}</div>
                                      <div className="text-sm text-muted-foreground">{schedule.config.customer.name}</div>
                                    </div>
                                    <div className="text-right text-sm ml-4">
                                      ₹{totalAssetValue.toLocaleString("en-IN")}
                                    </div>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {validationErrors.schedule}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>

                {/* Lender Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Select Lender <span className="text-destructive">*</span></Label>
                  <Tooltip open={!!shouldShowError('lender')}>
                    <TooltipTrigger asChild>
                      <div>
                        <Select
                          value={selectedLenderId}
                          onValueChange={(v) => {
                            setSelectedLenderId(v);
                            const lender = availableLenders?.find(l => l.institution === v);
                            setSelectedLender(lender || null);
                            markTouched('lender');
                          }}
                          disabled={!selectedScheduleId || isLendersLoading}
                        >
                          <SelectTrigger className={cn("h-auto p-3", shouldShowError('lender') && "border-destructive")}>
                            <SelectValue key={Math.floor(Math.random() * 10000)} placeholder={!selectedScheduleId ? "Select schedule first" : isLendersLoading ? "Loading lenders..." : "Choose a lender"}>
                              {selectedLender && (
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  {selectedLender.companyName}
                                </div>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {!isLendersLoading && availableLenders?.length === 0 && (
                              <div className="p-2 text-sm text-muted-foreground">No lenders found for this schedule</div>
                            )}
                            {availableLenders?.map((lender) => (
                              <SelectItem key={lender.institution + Math.floor(Math.random() * 10000)} value={lender.institution}>
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  {lender.companyName}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {validationErrors.lender}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              {/* Middle Row - Parameters & Live Calculations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 border-b">
                {/* Funding Parameters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Funding Parameters</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">

                    <div className="p-3 bg-muted/50 rounded-md border space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-sm font-medium">Funding Mode</Label>
                        {isOverridden && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                            Overridden from RS default ({selectedSchedule?.config.fundingType === "preGstDiscounting" ? "Pre GST" : selectedSchedule?.config.fundingType === "postGstDiscounting" ? "Post GST" : "Post TDS"})
                          </span>
                        )}
                      </div>
                      {selectedSchedule ? (
                        <>
                          <Select
                            value={effectiveFundingType}
                            onValueChange={(v) => {
                              const next = v as FundingType;
                              if (next === effectiveFundingType) return;
                              setFundingTypeOverride(next);
                              // Caps/share semantics change with funding mode — reset period table
                              setSelectedPeriods([]);
                              setIsPartialAssignment(false);
                              setLenderShares({});
                              setBulkAmount("");
                              setBulkFrom("");
                              setBulkTo("");
                            }}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="preGstDiscounting">Pre GST — Lender funds Base Rental only</SelectItem>
                              <SelectItem value="postGstDiscounting">Post GST — Lender funds Base Rental + GST</SelectItem>
                              <SelectItem value="postTdsDiscounting">Post TDS — Lender funds Base Rental − TDS + GST</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="text-xs text-muted-foreground">
                            {effectiveFundingType === "preGstDiscounting" && "Lender funds Base Rental only. GST collected by Astrafin from customer."}
                            {effectiveFundingType === "postGstDiscounting" && "Lender funds Base Rental + GST. Customer pays full amount to lender."}
                            {effectiveFundingType === "postTdsDiscounting" && "Lender funds Base Rental − TDS + GST. TDS retained by customer."}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-muted-foreground">Select a schedule first</div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="funding-limit">Funding Limit (%) <span className="text-destructive">*</span></Label>
                        <Tooltip open={!!shouldShowError('fundingLimit')}>
                          <TooltipTrigger asChild>
                            <Input
                              id="funding-limit"
                              type="number"
                              value={fundingLimitPercent}
                              onChange={(e) => setFundingLimitPercent(Number(e.target.value))}
                              onBlur={() => markTouched('fundingLimit')}
                              min="1"
                              max="100"
                              className={cn(shouldShowError('fundingLimit') && "border-destructive")}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                            <div className="flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {validationErrors.fundingLimit}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div>
                        <Label htmlFor="discount-rate">Discount Rate (% p.a.) <span className="text-destructive">*</span></Label>
                        <Tooltip open={!!shouldShowError('discountRate')}>
                          <TooltipTrigger asChild>
                            <Input
                              id="discount-rate"
                              type="number"
                              value={discountRate}
                              onChange={(e) => setDiscountRate(Number(e.target.value))}
                              onBlur={() => markTouched('discountRate')}
                              step="0.1"
                              className={cn(shouldShowError('discountRate') && "border-destructive")}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                            <div className="flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {validationErrors.discountRate}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    <div>
                      <Label>Assignment Date <span className="text-destructive">*</span></Label>
                      <div className="text-xs text-muted-foreground mb-1">
                        Defaults to schedule commencement date (can be overridden)
                      </div>
                      <Tooltip open={!!shouldShowError('assignmentDate')}>
                        <TooltipTrigger asChild>
                          <div>
                            {/* <Popover> */}
                            {/* <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn("w-full justify-start text-left font-normal mt-1", shouldShowError('assignmentDate') && "border-destructive")}
                              onClick={() => markTouched('assignmentDate')}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(assignmentDate, "PPP")}
                            </Button>
                          </PopoverTrigger> */}
                            {/* <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={assignmentDate}
                              onSelect={(date) => { date && setAssignmentDate(date); markTouched('assignmentDate'); }}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent> */}
                            {/* <CustomDatePicker
        selectedDate={assignmentDate ? new Date(assignmentDate) : undefined}
        onDateChange={(d: any) => setAssignmentDate(d?.toISOString().slice(0, 10) || "")}
      /> */}
                            {/* </Popover> */}

                            <div>
                              {/* <Label className="text-sm"> MRA Signed Date <span className="text-red-600">*</span></Label> */}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full justify-start text-left font-normal h-9 text-sm",
                                      shouldShowError("mraSignedDate") &&
                                      "border-destructive"
                                    )}
                                    onClick={() => markTouched("mraSignedDate")}
                                  >
                                    <CalendarIcon className="mr-2 h-3 w-3" />
                                    {assignmentDate
                                      ? format(parseLocalDateInput(assignmentDate), "PPP")
                                      : "Pick a date"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  align="start"
                                  sideOffset={4}
                                  className="w-auto p-0 z-[9999]"
                                  style={{ zIndex: 9999 }}
                                >


                                  <CustomDatePicker
                                    value={
                                      assignmentDate ? parseLocalDateInput(assignmentDate) : undefined
                                    }
                                    onChange={(d) => {
                                      if (d) {
                                        setAssignmentDate(format(startOfDay(d), "yyyy-MM-dd"));
                                        markTouched("mraSignedDate");
                                      } else {
                                        setAssignmentDate("");
                                        markTouched("mraSignedDate");
                                      }
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                              {shouldShowError("mraSignedDate") && (
                                <p className="text-xs text-red-600 mt-1">
                                  {validationErrors.mraSignedDate}
                                </p>
                              )}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                          <div className="flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {validationErrors.assignmentDate}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Lender Funding Amount */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">Lender Fee Amount</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="funding-type" className="text-sm">Type</Label>
                          <Select
                            value={lenderFeeType}
                            onValueChange={(v: "amount" | "percentage") => {
                              setLenderFeeType(v);
                              markTouched('lenderFeeAmount');
                              markTouched('lenderFeePercent');
                            }}
                          >
                            <SelectTrigger id="funding-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="amount">Amount</SelectItem>
                              <SelectItem value="percentage">Percentage</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor={lenderFeeType === "amount" ? "lender-fee-amount" : "lender-fee-percent"} className="text-sm">
                            {lenderFeeType === "amount" ? "Amount (₹)" : "Percentage (%)"}
                          </Label>
                          {lenderFeeType === "amount" ? (
                            <Tooltip open={!!shouldShowError('lenderFeeAmount')}>
                              <TooltipTrigger asChild>
                                <Input
                                  id="lender-fee-amount"
                                  type="number"
                                  value={lenderFeeAmount || ""}
                                  onChange={(e) => {
                                    setLenderFeeAmount(Number(e.target.value));
                                    markTouched('lenderFeeAmount');
                                  }}
                                  onBlur={() => markTouched('lenderFeeAmount')}
                                  min="0"
                                  step="0.01"
                                  placeholder="Enter amount"
                                  className={cn(shouldShowError('lenderFeeAmount') && "border-destructive")}
                                />
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                                <div className="flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {validationErrors.lenderFeeAmount}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip open={!!shouldShowError('lenderFeePercent')}>
                              <TooltipTrigger asChild>
                                <Input
                                  id="lender-fee-percent"
                                  type="number"
                                  value={lenderFeePercent || ""}
                                  onChange={(e) => {
                                    setLenderFeePercent(Number(e.target.value));
                                    markTouched('lenderFeePercent');
                                  }}
                                  onBlur={() => markTouched('lenderFeePercent')}
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="Enter percentage"
                                  className={cn(shouldShowError('lenderFeePercent') && "border-destructive")}
                                />
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                                <div className="flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {validationErrors.lenderFeePercent}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {lenderFeeType === "percentage" && totalAssetValue > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Calculated Amount: ₹{calculatedLenderFeeAmount.toLocaleString("en-IN")}
                        </div>
                      )}
                    </div>

                  </CardContent>
                </Card>

                {/* Live Calculation Dashboard */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Total Asset Value
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-xl font-bold">₹{totalAssetValue.toLocaleString("en-IN")}</div>
                        <div className="text-xs text-muted-foreground">Base + GST</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Funding Cap
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-xl font-bold">₹{fundingCap.toLocaleString("en-IN")}</div>
                        <div className="text-xs text-muted-foreground">
                          {fundingLimitPercent}% of total asset value
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wallet className="h-4 w-4" />
                          Total Funding
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className={cn(
                          "text-xl font-bold",
                          totalFunding >= totalAssetValue ? "text-green-600" : "text-red-600"
                        )}>
                          ₹{Math.round(totalFunding - calculatedLenderFeeAmount).toLocaleString("en-IN")}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          PV: ₹{Math.round(pvTotal).toLocaleString("en-IN")}<br />
                          SD: ₹{securityDeposit.toLocaleString("en-IN")}<br />
                          PF+GST: ₹{(processingFee + processingFeeGST).toLocaleString("en-IN")}<br />
                          Lender Fee: ₹{calculatedLenderFeeAmount.toLocaleString("en-IN")}
                        </div>
                        <div className={cn(
                          "text-xs mt-2",
                          totalFunding >= totalAssetValue ? "text-green-600" : "text-red-600"
                        )}>
                          {totalFunding >= totalAssetValue
                            ? "Good to go"
                            : `${shortfallPercent.toFixed(1)}% shortfall of fund`
                          }
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calculator className="h-4 w-4" />
                          PV Total
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-xl font-bold">₹{Math.round(pvTotal).toLocaleString("en-IN")}</div>
                        <div className="text-xs text-muted-foreground">
                          {selectedPeriods.length} periods
                        </div>
                        <PVCalculationModal
                          totalPV={Math.round(pvTotal).toLocaleString("en-IN")}
                          lines={linesForPV}
                          discountRate={discountRate}
                          assignmentDate={assignmentDate}
                          lenderFundsGST={partialActive ? false : lenderFundsGST}
                          lenderFundsTDS={partialActive ? false : lenderFundsTDS}
                        >
                          <Button variant="ghost" size="sm" className="mt-1 h-6 text-xs">
                            <Eye className="h-3 w-3 mr-1" />
                            View Details
                          </Button>
                        </PVCalculationModal>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calculator className="h-4 w-4" />
                          Variance
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className={cn(
                          "text-xl font-bold",
                          variance >= 0 ? "text-green-600" : "text-red-600"
                        )}>
                          ₹{variance.toLocaleString("en-IN")}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {variance >= 0 ? "Within limit" : "Exceeds cap"}
                        </div>
                      </CardContent>
                    </Card>


                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wallet className="h-4 w-4" />
                          Upfront Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {/* <p>{totalFunding} {processingFee * 1.18} {calculatedLenderFeeAmount} {totalAssetValue }</p> */}
                        <div className={cn(
                          "text-xl font-bold", (totalFunding + (processingFee * 1.18) - calculatedLenderFeeAmount - totalAssetValue) > 0 ? "text-green-600" : "text-red-600"
                        )}>

                          ₹{(totalFunding + (processingFee * 1.18) - calculatedLenderFeeAmount - totalAssetValue).toLocaleString("en-IN")}

                        </div>
                        {/* <div className="text-xs text-muted-foreground">
                      {calculatedLenderFeeAmount >= 0 ? "Within limit" : "Exceeds cap"}
                    </div> */}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={handleFitToFunding}
                      disabled={!selectedSchedule}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Fit to Funding
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (selectedSchedule) {
                          const ids = selectableLines.map((l) => l.scheduleLineId);
                          setSelectedPeriods(ids);
                          if (isPartialAssignment) {
                            setLenderShares((prev) => {
                              const next = { ...prev };
                              selectableLines.forEach((l) => {
                                if (next[l.scheduleLineId] == null) next[l.scheduleLineId] = capForLine(l);
                              });
                              return next;
                            });
                          }
                        }
                      }}
                      disabled={!selectedSchedule || selectableLines.length === 0}
                    >
                      Select All (Pending)
                    </Button>
                  </div>
                </div>
              </div>

              {/* Bottom Section - Period Selection Table */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">Select Rental Periods</h3>
                    <div className="flex items-center gap-2 pl-3 border-l">
                      <Split className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="partial-toggle" className="text-sm">Partial assignment</Label>
                      <Switch
                        id="partial-toggle"
                        checked={isPartialAssignment}
                        className="data-[state=checked]:bg-[#3F8B98]"
                        onCheckedChange={(v) => {
                          setIsPartialAssignment(v);
                          if (v) {
                            setLenderShares((prev) => {
                              const next = { ...prev };
                              selectedLines.forEach((l) => {
                                if (next[l.scheduleLineId] == null) next[l.scheduleLineId] = capForLine(l);
                              });
                              return next;
                            });
                          } else {
                            // Turning partial off — reset period selection and all share inputs
                            setSelectedPeriods([]);
                            setLenderShares({});
                            setBulkAmount("");
                            setBulkFrom("");
                            setBulkTo("");
                          }
                        }}
                      />
                      <span className="text-[11px] text-muted-foreground">
                        {isPreGST && "Cap per period: Base"}
                        {isPostGST && "Cap per period: Base + GST"}
                        {isPostTDS && "Cap per period: Base + GST − TDS"}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedPeriods.length} of {selectableLines.length} assignable periods selected
                  </div>
                </div>

                {partialActive && (
                  <div className="mb-3 p-3 rounded-lg border bg-muted/30 space-y-2">
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="text-xs font-medium mr-2">Bulk apply lender share</div>
                      <div>
                        <Label className="text-[11px]">Amount ₹</Label>
                        <Input className="h-8 w-28" type="number" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">From period</Label>
                        <Input className="h-8 w-20" type="number" value={bulkFrom} onChange={(e) => setBulkFrom(e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[11px]">To period</Label>
                        <Input className="h-8 w-20" type="number" value={bulkTo} onChange={(e) => setBulkTo(e.target.value)} />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const amt = Number(bulkAmount);
                          const from = Number(bulkFrom);
                          const to = Number(bulkTo);
                          if (!selectedSchedule || !Number.isFinite(amt) || amt <= 0) {
                            toast({
                              title: "Invalid amount",
                              description: "Enter a lender share amount greater than 0",
                              variant: "destructive",
                            });
                            return;
                          }
                          if (!Number.isFinite(from) || !Number.isFinite(to) || from < 1 || to < 1) {
                            toast({
                              title: "Invalid period range",
                              description: "Enter valid From and To period numbers",
                              variant: "destructive",
                            });
                            return;
                          }
                          const rangeStart = Math.min(from, to);
                          const rangeEnd = Math.max(from, to);

                          const matchingLines = selectableLines.filter((l) => {
                            const p = Number(l.periodNo);
                            return Number.isFinite(p) && p >= rangeStart && p <= rangeEnd;
                          });

                          if (matchingLines.length === 0) {
                            toast({
                              title: "No periods matched",
                              description: `No selectable periods found between ${rangeStart} and ${rangeEnd}`,
                              variant: "destructive",
                            });
                            return;
                          }

                          // Replace selection with this range only — periods outside (e.g. 7–10 after 1–6) are reset
                          setSelectedPeriods(matchingLines.map((l) => l.scheduleLineId));
                          setLenderShares(() => {
                            const next: Record<string, number> = {};
                            matchingLines.forEach((l) => {
                              next[l.scheduleLineId] = Math.min(amt, capForLine(l));
                            });
                            return next;
                          });

                          toast({
                            title: "Lender share applied",
                            description: `Applied ₹${Math.round(amt).toLocaleString("en-IN")} to periods ${rangeStart}–${rangeEnd} (${matchingLines.length} period(s)). Outside periods cleared.`,
                          });
                        }}
                      >
                        Apply
                      </Button>
                      <div className="ml-auto flex gap-4 text-xs">
                        <span>Lender: <b>₹{Math.round(splitSummary.lender).toLocaleString("en-IN")}</b></span>
                        <span>Astrafin: <b>₹{Math.round(splitSummary.astrafin).toLocaleString("en-IN")}</b></span>
                        <span>Split periods: <b>{splitSummary.splitCount}</b></span>
                      </div>
                    </div>
                    {splitSummary.hasInvalid && (
                      <div className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Each lender share must be greater than 0 and no more than the assignable cap for that period.
                      </div>
                    )}
                  </div>
                )}

                {selectedSchedule && assignableLines.length > 0 ? (
                  <ScrollArea className="h-80 border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Select</TableHead>
                          <TableHead>Period No</TableHead>
                          <TableHead>From Date</TableHead>
                          <TableHead>To Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-right">Base Rent</TableHead>
                          {lenderFundsTDS && <TableHead className="text-right">TDS</TableHead>}
                          <TableHead className="text-right">GST</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          {partialActive && <TableHead className="text-right">Lender Share</TableHead>}
                          {partialActive && <TableHead className="text-right">Astrafin Retained</TableHead>}
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {assignableLines.map((line) => {
                          const canSelect = isSelectableRentalLine(line);
                          const isSelected = canSelect && selectedPeriods.includes(line.scheduleLineId);
                          const totalGST = line.gstRaw;
                          const totalAfterTDS = lenderFundsTDS
                            ? (line.baseRentRounded + (line.gstRaw ?? 0) - line.tdsRounded)
                            : (line.baseRentRounded + (line.gstRaw ?? 0));
                          const cap = capForLine(line);
                          const share = lenderShares[line.scheduleLineId] ?? (isSelected ? cap : 0);
                          const retained = isSelected ? Math.max(0, cap - Math.min(share, cap)) : 0;
                          const invalidShare = partialActive && isSelected && (share <= 0 || share > cap);
                          const assignedStatus = getAssignmentStatus(
                            line.scheduleLineId,
                            Math.min(share, cap),
                            cap,
                            isSelected
                          );

                          return (
                            <TableRow
                              key={line.scheduleLineId}
                              className={cn(
                                isSelected ? "bg-muted/50" : "",
                                !canSelect && "opacity-60"
                              )}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={!canSelect}
                                  onCheckedChange={(checked) => {
                                    if (!canSelect) return;
                                    if (checked === true) {
                                      handleAssignedStatusChange(line.scheduleLineId, "A", cap);
                                    } else {
                                      handleAssignedStatusChange(line.scheduleLineId, "N", cap);
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {line.periodNo}
                              </TableCell>
                              <TableCell>
                                {format(new Date(line.fromDate), "MMM dd, yyyy")}
                              </TableCell>
                              <TableCell>
                                {format(new Date(line.toDate), "MMM dd, yyyy")}
                              </TableCell>
                              <TableCell>
                                {format(new Date(line.dueDate), "MMM dd, yyyy")}
                              </TableCell>
                              <TableCell className="text-right">
                                ₹{line.baseRentRounded.toLocaleString("en-IN")}
                              </TableCell>
                              {lenderFundsTDS && (
                                <TableCell className="text-right text-red-600">
                                  -₹{line.tdsRounded.toLocaleString("en-IN")}
                                </TableCell>
                              )}
                              <TableCell className="text-right">
                                ₹{totalGST.toLocaleString("en-IN")}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                ₹{totalAfterTDS.toLocaleString("en-IN")}
                              </TableCell>
                              {partialActive && (
                                <TableCell className="text-right">
                                  <Input
                                    type="number"
                                    className={cn("h-8 w-28 ml-auto text-right", invalidShare && "border-destructive")}
                                    value={isSelected ? share : ""}
                                    disabled={!isSelected}
                                    min={0}
                                    max={cap}
                                    placeholder="0"
                                    onChange={(e) => {
                                      const v = Number(e.target.value);
                                      setLenderShares((prev) => ({
                                        ...prev,
                                        [line.scheduleLineId]: isNaN(v) ? 0 : v,
                                      }));
                                    }}
                                  />
                                </TableCell>
                              )}
                              {partialActive && (
                                <TableCell className="text-right text-muted-foreground">
                                  ₹{(retained).toLocaleString("en-IN")}
                                </TableCell>
                              )}
                              <TableCell className="text-center">
                                <Select
                                  value={assignedStatus}
                                  disabled={!canSelect}
                                  onValueChange={(v) =>
                                    handleAssignedStatusChange(
                                      line.scheduleLineId,
                                      v as "A" | "P" | "N",
                                      cap
                                    )
                                  }
                                >
                                  <SelectTrigger
                                    className={cn(
                                      "h-8 w-[100px] mx-auto text-xs font-semibold",
                                      assignedStatus === "A" && "border-emerald-200 bg-emerald-50 text-emerald-800",
                                      assignedStatus === "P" && "border-amber-200 bg-amber-50 text-amber-800",
                                      assignedStatus === "N" && "text-muted-foreground"
                                    )}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="A">Fully</SelectItem>
                                    {partialActive && <SelectItem value="P">Partial</SelectItem>}
                                    <SelectItem value="N">None</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                ) : selectedSchedule && assignableLines.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-muted-foreground border rounded-lg">
                    <div className="text-center">
                      <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>This schedule has no regular rental periods for NoA assignment.</p>
                      <p className="text-xs mt-1">Interim periods (period i) are excluded.</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground border rounded-lg">
                    <div className="text-center">
                      <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Select a schedule to view rental periods</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer with Action Button */}
              <div className="border-t p-6 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {showAllErrors && !isFormValid && (
                      <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        {Object.values(validationErrors)[0]}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleCreateNoA}
                    disabled={(showAllErrors && !isFormValid) || isCreating}
                    size="lg"
                    className="min-w-48 flex items-center gap-2"
                  >
                    {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isCreating ? 'Creating...' : 'Generate Notice of Assignment'}
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>
        </TooltipProvider>
      </DrawerContent>
    </Drawer>
  );
}